/**
 * RegistrationConfirmation Component
 * 
 * Displays a confirmation message after successful registration submission,
 * informing the user that a confirmation email has been sent with an activation link.
 * 
 * @returns {JSX.Element} Registration confirmation page
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';

const RegistrationConfirmation = () => {
  const navigate = useNavigate();

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <div className="card card-conference shadow">
            <div className="card-header bg-conference-gradient text-white text-center">
              <h2 className="mb-0 text-white">
                <i className="bi bi-check-circle me-2"></i>
                Registration Submitted
              </h2>
            </div>

            <div className="card-body p-4 text-center">
              {/* Success Icon */}
              <div className="mb-4">
                <i 
                  className="bi bi-envelope-check text-conference-primary"
                  style={{ fontSize: '4rem' }}
                ></i>
              </div>

              {/* Main Message */}
              <h4 className="text-conference-primary mb-3">
                Thank you for registering!
              </h4>

              <p className="lead mb-4">
                Your registration has been successfully submitted.
              </p>

              {/* Email Confirmation Info */}
              <div className="alert alert-info" role="alert">
                <i className="bi bi-info-circle me-2"></i>
                <strong>Confirmation Email Sent</strong>
                <p className="mb-0 mt-2">
                  A confirmation email has been sent to your registered email address.
                  <br/>
                  Please check your inbox and <strong>click the activation link</strong> to complete the setup of your account.
                </p>
              </div>

              {/* Additional Instructions */}
              <div className="row mt-4">
                <div className="col-md-6 mb-3">
                  <div className="border rounded p-3 h-100">
                    <i className="bi bi-search text-conference-primary" style={{ fontSize: '1.5rem' }}></i>
                    <h6 className="mt-2">Don’t see the email?</h6>
                    <small className="text-muted">
                      Check your spam or junk folder—it may have been filtered there.
                    </small>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="border rounded p-3 h-100">
                    <i className="bi bi-link-45deg text-conference-primary" style={{ fontSize: '1.5rem' }}></i>
                    <h6 className="mt-2">Next Steps</h6>
                    <small className="text-muted">
                      <ol class="text-start">
                        <li>Check Your Email.</li>
                        <li>Look for a message from MF Society.</li>
                        <li>Activate Your Account.</li>
                        <li>Click the activation link included in the email.</li>
                        <li>Your account will remain inactive until this step is completed.</li>
                      </ol>
                    </small>
                  </div>
                </div>
              </div>

              {/* Important Note */}
              <div className="alert alert-warning mt-4" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>Important:</strong> Your account will remain inactive until you click the activation link in the confirmation email.
              </div>

              {/* Action Buttons */}
              <div className="mt-4">
                <button
                  type="button"
                  className="btn btn-conference-primary me-3"
                  onClick={handleBackToLogin}
                >
                  <i className="bi bi-box-arrow-in-right me-2"></i>
                  Go to Login
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleBackToHome}
                >
                  <i className="bi bi-house me-2"></i>
                  Back to Home
                </button>
              </div>

              {/* Support Information */}
              <div className="mt-5 pt-4 border-top">
                <h6 className="text-muted mb-2">Need Help?</h6>
                <p className="text-muted small mb-0">
                  If you do not receive a confirmation email within a few minutes, 
                  please get in touch with us at mfs@mfsociety.org
                  <a 
                    href="mailto:mfc@mfsociety.org" 
                    className="text-conference-primary text-decoration-none"
                  >
                    mfc@mfsociety.org
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationConfirmation;
